import { api } from '../lib/axios';
import { LabelDesign } from '../types/enhanced-product-info.types';

export interface PrintPackLabel {
  sku?: string;
  batchId?: string;
  serial: string | number;
  verifyUrl: string;
  qrPayload?: string;
  humanText?: string;
  producerName?: string;
  variant?: 'outer' | 'inner';
}

export type ThermalLabelSize = '2x1' | '2x1.5' | '2x2' | '50x30mm' | '50x34mm';

export interface ThermalLabelRequest {
  size: ThermalLabelSize;
  labelDesign?: LabelDesign;
  qrData?: string;
  title?: string;
  subtitle?: string;
  producerName?: string;
  labelType?: string;
  mrp?: number | string;
  labels?: Array<{
    qrData: string;
    title?: string;
    subtitle?: string;
    producerName?: string;
    labelType?: string;
    mrp?: number | string;
  }>;
  dpi?: 203 | 300 | 600;
  marginMm?: number;
  includePdf?: boolean;
}

export interface ThermalLabelResponse {
  success: boolean;
  widthPx?: number;
  heightPx?: number;
  dpi?: number;
  pngBase64?: string;
  pdfBase64?: string;
  zipBase64?: string;
  count: number;
  error?: string;
}

export interface PrintPackRequest {
  jobId: string;
  labels: PrintPackLabel[];
  layout: 'sheet' | 'roll';
  pageSize: 'A4' | 'Letter';
  labelSizeMm: { width: number; height: number };
  dpi: 203 | 300 | 600;
  printerType?: 'ZPL' | 'TSPL' | 'SBPL' | 'NONE';
  printerTarget?: { host: string; port?: number; protocol: 'RAW9100' | 'LPD' | 'HTTP' };
  includePreviewSvg?: boolean;
}

export interface PrintPackResponse {
  success: boolean;
  manifest?: any;
  pdfBase64?: string;
  zipBase64?: string;
  previewSvg?: string;
  directPrint?: { attempted: boolean; success: boolean; message?: string };
  error?: string;
}

export interface PrintPackJobStartResponse {
  success: boolean;
  jobId?: string;
  error?: string;
}

export interface PrintPackJobStatusResponse {
  success: boolean;
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  download?: { pdf?: string; zip?: string };
}

class PrintPackService {
  async generate(payload: PrintPackRequest): Promise<PrintPackResponse> {
    const { data } = await api.post('/print-pack', payload);
    return data;
  }

  async generateThermalLabel(payload: ThermalLabelRequest): Promise<ThermalLabelResponse> {
    const { data } = await api.post('/print-pack/thermal-label', payload);
    return data;
  }

  async startJob(payload: PrintPackRequest): Promise<PrintPackJobStartResponse> {
    const { data } = await api.post('/print-pack/jobs', payload);
    return data;
  }

  async getJobStatus(jobId: string): Promise<PrintPackJobStatusResponse> {
    const { data } = await api.get(`/print-pack/jobs/${jobId}/status`);
    return data;
  }
}

export const printPackService = new PrintPackService();
